import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback, createRef } from 'react';
import { usePostMessage, useSubscribeData } from 'spectacle-ui-game-bridge-react';
import { PreloginContent } from '../../hooks/useContentForBuild';
import AuthCode, { AuthCodeRef } from 'react-auth-code-input';
import Loader from '../../components/loader/loader';
import Modal from '../modal/modal';
import useBackButton from '../../hooks/useBackButton';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';
import useFocusInputs from '../../hooks/useFocusInputs';
import './twoFactorAuth.sass';

interface TwoFactorAuthComponent {
  content: PreloginContent | null;
  isShown: boolean;
  toggle: () => void;
}

export default function TwoFactorAuth({ content, isShown, toggle }: TwoFactorAuthComponent) {
  const navigate = useNavigate();
  const formElement = createRef<HTMLFormElement>();
  const authInputRef = useRef<AuthCodeRef>(null);
  const [setOffset, offsetStyle, onFocus, onBlur] = useFocusInputs();
  const [isThrottled, setIsThrottled] = useState<boolean>(false);
  const [isShowingLoader, setIsShowingLoader] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const sendOTPAndResumeLogin = usePostMessage('sendOTPAndResumeLogin');
  const closeWebView = usePostMessage('closeWebView');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const accountRegistrationStatus = useSubscribeData<string>('tos', 'AccountRegistrationStatus');
  const authThrottleTime = useSubscribeData<number>('tos', 'authThrottleTime');
  const dlog = useDlogLoginFunnel();
  const timer: any = useRef(null);
  useBackButton(isShown, toggle);

  const authInputWrapperRef = useCallback(
    (node: any) => {
      if (node !== null) {
        setOffset('authcode', node.getBoundingClientRect().top);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleOnChange = (res: string) => {
    setCode(res);
  };

  const submit2fa = (e: any) => {
    e.preventDefault();
    setIsShowingLoader(true);
    sendOTPAndResumeLogin(code);
    (document.activeElement as HTMLElement).blur();

    dlog({
      phase: 'Login_Screen_2FA',
      action: 'Click',
      targets: 'Submit',
    });
  };

  useEffect(() => {
    if (isShown) {
      switch (accountRegistrationStatus) {
        case 'AccountRegistrationNeedsOTP':
          dlog({
            phase: 'Login_Screen_2FA',
            action: 'Error',
          });
          setIsShowingLoader(false);
          break;
        case 'AccountRegistrationSuccess':
          dlog({
            phase: 'Login_Screen_2FA',
            action: 'Success',
          });
          passPreloginFence();
          if (content?.enablePostLoginMessages) {
            navigate('/post-login');
          } else {
            closeWebView();
          }
          break;
        default:
          break;
      }

      // If throtttled lock this input until its not throttled anymore
      setIsThrottled(accountRegistrationStatus === 'AccountRegistrationOTPThrottled');
    }
  }, [accountRegistrationStatus, passPreloginFence, closeWebView, dlog, isShown, content, navigate]);

  // If we are throttled, set a timer to unlock the input
  useEffect(() => {
    if (authThrottleTime && authThrottleTime > 0 && !timer) {
      timer.current = setTimeout(() => {
        setIsThrottled(false);
      }, authThrottleTime);
    }

    return () => clearTimeout(timer.current);
  }, [authThrottleTime]);

  useEffect(() => {
    if (isShown) {
      dlog({
        phase: 'Login_Screen_2FA',
        action: 'Render',
      });
    }
  }, [isShown, dlog]);

  useEffect(() => {
    if (isShown && isThrottled) {
      dlog({
        phase: 'Login_Screen_2FA',
        action: 'Error',
        targets: '2FA_Too_Many_Attempts',
      });
    }
  }, [isShown, isThrottled, dlog]);

  // Assign Focus Blur for Keyboard Dismissal
  useEffect(() => {
    document.body.addEventListener('atvi_keyboard_dismiss', onBlur);

    return () => {
      document.body.removeEventListener('atvi_keyboard_dismiss', onBlur);
    };
  }, [onBlur]);

  return (
    <>
      <AnimatePresence>
        {isShown && content && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Modal style={offsetStyle} toggle={toggle} additionalClass="frame--50-center">
              <Loader isShown={isShowingLoader} />
              <div className="twofactor">
                <h3>{content.enterTheSecurityCode}</h3>
                <p>{content.newLocation}</p>
                {isThrottled && <p>{content.tooManyAttempts.replace('&&1', content.fewMinutes)}</p>}
                <form ref={formElement} onSubmit={submit2fa} noValidate autoComplete="off">
                  <div ref={authInputWrapperRef} onFocus={onFocus} data-title="authcode">
                    <AuthCode containerClassName="twofactor__authcode" onChange={handleOnChange} allowedCharacters="numeric" autoFocus={false} ref={authInputRef} disabled={isShowingLoader} />
                  </div>
                  <button className="cta cta--full" onClick={submit2fa} disabled={isThrottled || code.length < 6 || isShowingLoader}>
                    {content.continue}
                  </button>
                </form>
              </div>
            </Modal>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
